/**
 * Feature flags and metered plan limits, backed by Convex.
 *
 * The Supabase version was 415 lines and permanently broken: it read a
 * `feature_flags` table that was never created, `fetchFeatureData()` bailed on
 * the first error, and the flag map stayed empty — so `isFeatureEnabled()`
 * always returned false and anything gated behind a flag (`canCreateProject`,
 * for one) could never succeed. It also called two Postgres RPCs the browser
 * can no longer reach under Clerk.
 *
 * The public surface here is unchanged so the existing call sites keep working.
 * Two semantics are deliberately different, both fixing bugs:
 *
 * - **A flag is enabled unless a row disables it.** A kill-switch table that is
 *   empty should mean "nothing is switched off", not "nothing works".
 * - **An unmetered feature is unlimited, not forbidden.** A missing `roleLimits`
 *   row can no longer lock a paying user out of a feature.
 *
 * Period keys are the *caller's* local calendar — "today" in Berlin is not
 * "today" on the server — so they are computed here and passed in.
 */
import { api } from '@/convex/_generated/api'
import { convex, whenConvexReady } from '@/lib/convex-client'

export type LimitType = 'daily' | 'monthly' | 'total'

export interface FeatureUsageCheck {
  allowed: boolean
  limit: number
  current_usage: number
  remaining: number
  /** Plan the limit was resolved against — used in "limit reached" copy. */
  user_role: string
}

/** Local-calendar period key: "2026-07-30" daily, "2026-07" monthly, "all" total. */
function periodKey(limitType: LimitType): string {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')

  if (limitType === 'daily') {
    return `${year}-${month}-${String(now.getDate()).padStart(2, '0')}`
  }
  if (limitType === 'monthly') {
    return `${year}-${month}`
  }
  return 'all'
}

const UNMETERED: FeatureUsageCheck = {
  allowed: true,
  limit: -1,
  current_usage: 0,
  remaining: -1,
  user_role: 'basic',
}

class FeatureFlagService {
  private flags: Record<string, boolean> | null = null

  /** Loads the flag map once; cheap enough to re-request when cleared. */
  async fetchFeatureData(): Promise<void> {
    try {
      await whenConvexReady()
      this.flags = await convex.query(api.limits.featureFlags, {})
    } catch (error) {
      console.error('[feature-flags] could not load flags:', error)
      this.flags = null
    }
  }

  clearCache(): void {
    this.flags = null
  }

  async isFeatureEnabled(flagKey: string): Promise<boolean> {
    if (this.flags === null) {
      await this.fetchFeatureData()
    }
    // Absent means "not switched off".
    return this.flags?.[flagKey] ?? true
  }

  async checkUserLimit(
    featureKey: string,
    limitType: LimitType = 'daily',
  ): Promise<FeatureUsageCheck> {
    try {
      await whenConvexReady()
      const result = await convex.query(api.limits.checkFeature, {
        featureKey,
        limitType,
        periodStart: periodKey(limitType),
      })
      return {
        allowed: result.allowed,
        limit: result.limit,
        current_usage: result.current_usage,
        remaining: result.remaining,
        user_role: result.role,
      }
    } catch (error) {
      console.error(`[feature-flags] limit check failed for ${featureKey}:`, error)
      // Fail open: a metering outage must not block paid functionality.
      return UNMETERED
    }
  }

  async recordFeatureUsage(featureKey: string, limitType: LimitType = 'daily'): Promise<number> {
    try {
      await whenConvexReady()
      return await convex.mutation(api.limits.recordUsage, {
        featureKey,
        limitType,
        periodStart: periodKey(limitType),
      })
    } catch (error) {
      console.error(`[feature-flags] could not record usage for ${featureKey}:`, error)
      return 0
    }
  }

  /**
   * The caller's own allowance. `_role` is ignored — the plan is resolved
   * server-side, so a client cannot ask for another plan's limits.
   */
  async getRoleLimit(
    _role: string,
    featureKey: string,
    limitType: LimitType = 'daily',
  ): Promise<number> {
    return (await this.checkUserLimit(featureKey, limitType)).limit
  }

  /** Checks the limit and, if there's room, consumes one unit. */
  async checkAndRecordUsage(
    featureKey: string,
    limitType: LimitType = 'daily',
  ): Promise<FeatureUsageCheck> {
    const usage = await this.checkUserLimit(featureKey, limitType)
    if (!usage.allowed) {
      return usage
    }

    const newUsageCount = await this.recordFeatureUsage(featureKey, limitType)
    return {
      ...usage,
      current_usage: newUsageCount,
      remaining: FeatureLimitUtils.calculateRemaining(usage.limit, newUsageCount),
    }
  }

  async canCreateProject(): Promise<boolean> {
    const [enabled, usage] = await Promise.all([
      this.isFeatureEnabled('projects'),
      this.checkUserLimit('projects', 'total'),
    ])
    return enabled && usage.allowed
  }
}

export const featureFlagService = new FeatureFlagService()

/**
 * A product gate, not a technical one: the AI assistant stays off until it
 * ships. Flip this deliberately rather than as a side effect of other work.
 */
export const FEATURES = {
  AI_ENABLED: false,
}

export const FeatureChecks = {
  canUseAI: () => featureFlagService.checkUserLimit('ai_suggestions', 'daily'),
  canUseTimer: () => featureFlagService.checkUserLimit('timer_tasks', 'daily'),
  canExportData: () => featureFlagService.checkUserLimit('export_requests', 'monthly'),

  useAI: () => featureFlagService.checkAndRecordUsage('ai_suggestions', 'daily'),
  useTimer: () => featureFlagService.checkAndRecordUsage('timer_tasks', 'daily'),
  useExport: () => featureFlagService.checkAndRecordUsage('export_requests', 'monthly'),
}

export const FeatureLimitUtils = {
  calculateRemaining(limit: number, currentUsage: number): number {
    return limit === -1 ? -1 : Math.max(0, limit - currentUsage)
  },

  isUnlimited(limit: number): boolean {
    return limit === -1
  },

  formatLimit(limit: number): string {
    return limit === -1 ? '∞' : limit.toString()
  },

  generateLimitErrorMessage(limit: number, featureName: string, userRole: string): string {
    const pluralSuffix = limit === 1 ? '' : 's'
    return `You've reached your daily limit of ${limit} ${featureName}${pluralSuffix} on the ${userRole} plan.`
  },
}
